import React, { useState } from 'react';
import { Tooltip } from '@dhis2/ui';

export const createRatingComponents = (RATING_DEFINITIONS) => {
  
  const EnhancedScoreRow = ({ 
    label, 
    hint, 
    value, 
    onChange, 
    weight = '50', 
    disabled = false, 
    score = '',
    showRatingHelp = true,
    scaleType = 'business'  // 'business' or 'leadership'
  }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    
    const getRatingInfo = (ratingValue) => {
      const ratingKey = String(ratingValue || '0');
      return RATING_DEFINITIONS[ratingKey] || RATING_DEFINITIONS['0'] || { 
        label: 'Not Rated', 
        color: '#6c757d', 
        bgColor: '#e9ecef',
        shortDesc: 'Not evaluated' 
      };
    };
    
    const ratingInfo = getRatingInfo(value);
    // Use light green for business scale when rating is 4-5
    const isBusinessHighScore = scaleType === 'business' && (value === '4' || value === '5');
    const bgColor = isBusinessHighScore ? '#d4edda' : (ratingInfo.bgColor || ratingInfo.color + '20');
    
    return (
      <div className="apr-score-row">
        <div className="apr-score-cell-main">
          <div className="apr-score-label">
            {label}
            {showRatingHelp && (
              <span 
                style={{ 
                  marginLeft: '8px', 
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: '#1565c0'
                }}
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              >
                ⓘ
              </span>
            )}
          </div>
          {hint && <div className="apr-score-hint">{hint}</div>}
          
          {showTooltip && showRatingHelp && !disabled && (
            <div style={{
              position: 'absolute',
              backgroundColor: 'white',
              border: '1px solid #ccc',
              borderRadius: '8px',
              padding: '12px',
              marginTop: '4px',
              zIndex: 1000,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              minWidth: '280px'
            }}>
              <strong style={{ display: 'block', marginBottom: '8px' }}>
                {scaleType === 'business' ? 'Business Objectives Rating Scale:' : 'Leadership Rating Scale:'}
              </strong>
              {Object.entries(RATING_DEFINITIONS)
                .sort(([a], [b]) => Number(b) - Number(a))
                .map(([key, def]) => (
                  <div key={key} style={{ 
                    marginBottom: '8px',
                    padding: '8px',
                    borderRadius: '6px',
                    backgroundColor: def.bgColor || def.color + '10',
                    borderLeft: `3px solid ${def.color}`,
                  }}>
                    <strong>{def.label}</strong>
                    <div style={{ fontSize: '11px', color: '#555', marginTop: '2px' }}>
                      {def.description || def.shortDesc}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
        
        <div className="apr-score-cell-rating">
          {!disabled && showRatingHelp ? (
            <select
              value={String(value ?? '')}
              onChange={(e) => onChange(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: `1px solid ${ratingInfo.color}`,
                backgroundColor: bgColor,
                cursor: 'pointer',
                fontWeight: value ? '500' : 'normal'
              }}
            >
              <option value="">Select rating...</option>
              {Object.entries(RATING_DEFINITIONS)
                .sort(([a], [b]) => Number(b) - Number(a))
                .map(([key, def]) => (
                  <option key={key} value={key}>
                    {def.label}
                  </option>
                ))}
            </select>
          ) : (
            <div style={{
              padding: '8px',
              backgroundColor: bgColor,
              borderRadius: '4px',
              textAlign: 'center',
              border: `1px solid ${ratingInfo.color}`,
              fontWeight: value ? 'bold' : 'normal'
            }}>
              {value ? (
                <span>
                  <span style={{ color: ratingInfo.color, fontWeight: 'bold' }}>{ratingInfo.label}</span>
                  <span style={{ fontSize: '11px', display: 'block', color: '#666', marginTop: '2px' }}>
                    {ratingInfo.shortDesc}
                  </span>
                </span>
              ) : (
                '— Not rated —'
              )}
            </div>
          )}
        </div>
        
        <div className="apr-score-cell-weight">{weight}%</div>
        <div className="apr-score-cell-result" style={{ fontWeight: 'bold', color: ratingInfo.color }}>
          {score}
        </div>
      </div>
    );
  };

  const CriteriaSection = ({ 
    title, 
    description, 
    children,
    collapsible = false,
    defaultExpanded = true
  }) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    
    return (
      <div className="apr-criteria-section" style={{
        marginBottom: '24px',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        <div 
          style={{
            padding: '12px 16px',
            backgroundColor: '#f8f9fa',
            borderBottom: expanded ? '1px solid #e0e0e0' : 'none',
            cursor: collapsible ? 'pointer' : 'default',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
          onClick={() => collapsible && setExpanded(!expanded)}
        >
          <div>
            <h5 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>{title}</h5>
            {description && <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '4px' }}>{description}</div>}
          </div>
          {collapsible && (
            <span style={{ fontSize: '1.2rem' }}>{expanded ? '▼' : '▶'}</span>
          )}
        </div>
        
        {(!collapsible || expanded) && (
          <div style={{ padding: '16px' }}>
            {children}
          </div>
        )}
      </div>
    );
  };

  return { EnhancedScoreRow, CriteriaSection };
};